import React, { useState, useEffect } from 'react';
import { FiCheck, FiClock, FiAlertCircle, FiCheckCircle, FiTrendingUp, FiCreditCard, FiCalendar, FiShield, FiZap, FiStar, FiPackage } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VendorSubscription = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysRemaining, setDaysRemaining] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, requestsRes] = await Promise.all([
        api.get('/vendor/subscription/plans'),
        api.get('/vendor/subscription/my-requests')
      ]);

      if (plansRes.data.success) {
        setPlans(plansRes.data.data.plans);
        setCurrentSubscription(plansRes.data.data.currentSubscription);
        
        // Calculate days remaining
        if (plansRes.data.data.currentSubscription?.endDate) {
          const endDate = new Date(plansRes.data.data.currentSubscription.endDate);
          const today = new Date();
          const diffTime = endDate - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysRemaining(diffDays);
        }
      }

      if (requestsRes.data.success) {
        setMyRequests(requestsRes.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch subscription data');
    } finally {
      setLoading(false);
    }
  };

  const requestPlan = async (planId) => {
    try {
      const response = await api.post('/vendor/subscription/request', { planId });
      if (response.data.success) {
        toast.success('Plan request submitted successfully!');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit request');
    }
  };

  const getPlanIcon = (planName) => {
    const name = planName.toLowerCase();
    if (name.includes('free')) return <FiPackage />;
    if (name.includes('basic')) return <FiShield />;
    if (name.includes('premium')) return <FiStar />;
    if (name.includes('enterprise')) return <FiZap />;
    return <FiCreditCard />;
  };

  const getPlanColor = (planName) => {
    const name = planName.toLowerCase();
    if (name.includes('free')) return '#6c757d';
    if (name.includes('basic')) return '#2596be';
    if (name.includes('premium')) return '#ff9800';
    if (name.includes('enterprise')) return '#4caf50';
    return '#2596be';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  const hasPendingRequest = myRequests.some(req => req.status === 'pending');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-4 md:p-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl shadow-lg p-8 mb-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <FiCreditCard className="text-3xl" />
            <h1 className="text-3xl md:text-4xl font-bold">Subscription Plans</h1>
          </div>
          <p className="text-lg opacity-90">
            Manage your subscription and upgrade your plan to unlock more features
          </p>
        </div>
      </div>

      {/* Current Subscription Card */}
      {currentSubscription && currentSubscription.plan && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-8 relative">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
                Current Subscription
              </h3>
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center`} style={{ backgroundColor: getPlanColor(currentSubscription.plan.name) + '20', color: getPlanColor(currentSubscription.plan.name) }}>
                  {getPlanIcon(currentSubscription.plan.name)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {currentSubscription.plan.name}
                  </h2>
                  <p className="text-gray-600 text-lg">
                    ₹{currentSubscription.plan.price} / {currentSubscription.plan.duration} days
                  </p>
                  {currentSubscription.plan.gstPercentage > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      + ₹{((currentSubscription.plan.price * currentSubscription.plan.gstPercentage) / 100).toFixed(2)} GST ({currentSubscription.plan.gstPercentage}%)
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="text-right">
              {daysRemaining !== null && (
                <>
                  {daysRemaining > 3 ? (
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-xl flex items-center gap-4 mb-3 shadow-lg">
                      <FiCheckCircle size={22} />
                      <div>
                        <div className="text-xl font-bold">
                          {daysRemaining} days left
                        </div>
                        <div className="text-sm opacity-90">
                          Your subscription is active
                        </div>
                      </div>
                    </div>
                  ) : daysRemaining > 0 ? (
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-3 rounded-xl flex items-center gap-3 mb-2">
                      <FiAlertCircle />
                      <div>
                        <div className="text-lg font-bold">{daysRemaining}</div>
                        <div className="text-xs opacity-90">Days Left!</div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-3 rounded-xl flex items-center gap-3 mb-2">
                      <FiAlertCircle />
                      <div>
                        <div className="text-lg font-bold">Expired</div>
                        <div className="text-xs opacity-90">Renew Now</div>
                      </div>
                    </div>
                  )}
                  <p className="text-sm text-gray-600 flex items-center justify-end">
                    <FiCalendar className="mr-2" />
                    Expires: {new Date(currentSubscription.endDate).toLocaleDateString()}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pending Requests Alert */}
      {myRequests.filter(req => req.status === 'pending').length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
              <FiClock className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-amber-800 mb-3">
                Pending Plan Requests
              </h4>
              {myRequests.filter(req => req.status === 'pending').map(req => (
                <div key={req._id} className="bg-white rounded-lg p-4 mb-3 border border-orange-200">
                  <div className="flex justify-between items-center mb-2">
                    <strong className="text-gray-900">{req.requestedPlan?.name}</strong>
                    <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-xs font-semibold">
                      Pending
                    </span>
                  </div>
                  <small className="text-gray-600">
                    Requested on {new Date(req.requestedAt).toLocaleDateString()}
                  </small>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Available Plans Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <FiTrendingUp className="text-2xl text-gray-700" />
          <h2 className="text-2xl font-bold text-gray-900">
            Available Plans
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = currentSubscription?.plan?._id === plan._id;
            const planColor = getPlanColor(plan.name);
            
            return (
              <div 
                key={plan._id} 
                className={`bg-white rounded-xl p-8 relative transition-all duration-300 hover:shadow-xl ${
                  isCurrentPlan ? 'border-4 shadow-2xl scale-105' : 'border border-gray-200 shadow-sm'
                }`}
                style={{ borderColor: isCurrentPlan ? planColor : undefined }}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg">
                    CURRENT PLAN
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 text-2xl`} style={{ backgroundColor: planColor + '20', color: planColor }}>
                    {getPlanIcon(plan.name)}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  {plan.gstPercentage > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      + ₹{((plan.price * plan.gstPercentage) / 100).toFixed(2)} GST ({plan.gstPercentage}%)
                    </p>
                  )}
                </div>
                
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-lg text-gray-600 font-medium">₹</span>
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">/{plan.duration} days</span>
                  </div>
                  {plan.gstPercentage > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      Total: ₹{(plan.price + (plan.price * plan.gstPercentage) / 100).toFixed(2)}
                    </p>
                  )}
                </div>

                <div className="mt-auto">
                  {!isCurrentPlan ? (
                    <button 
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 ${
                        hasPendingRequest 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-60' 
                          : 'text-white hover:shadow-lg'
                      }`}
                      style={{ backgroundColor: hasPendingRequest ? undefined : planColor }}
                      onClick={() => requestPlan(plan._id)}
                      disabled={hasPendingRequest}
                    >
                      {hasPendingRequest ? 'Request Pending' : 'Request This Plan'}
                    </button>
                  ):(
                    <button 
                      className="w-full py-3 px-6 bg-gray-200 text-gray-700 rounded-lg font-semibold cursor-not-allowed"
                      disabled={true}
                    >
                      Current Plan
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Request History */}
      {myRequests.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            Request History
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Requested Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {myRequests.map(req => (
                  <tr key={req._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{req.requestedPlan?.name}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {new Date(req.requestedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        req.status === 'approved' ? 'bg-green-100 text-green-800' : 
                        req.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {req.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorSubscription;
